import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { addRelationshipMapping } from 'common/api';
import { ReactComponent as Regenerate } from 'common/assets/images/ai-generate.svg';
import { ReactComponent as Plus } from 'common/assets/images/Plus.svg';
import { MODULE_NAME } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	ENTITY_STATUS,
	PRC_BUTTON_TEXT,
	PRC_TOAST_MSG,
} from 'common/constants/TaxonomyConstants';
import { RootState } from 'common/store/AppStore';
import { setIsPRCDetailsPage } from 'common/store/reducer/commonReducer';
import { setAddedRelationshipMappingIds } from 'common/store/reducer/taxonomyReducer';

import styles from '../Overview/Overview.module.scss';
import {
	fetchAllTabsData,
	fetchToastMsgData,
	getChildEntityOptionsData,
} from '../TaxonomyUtils';

import ButtonComp from '../../../../common/components/Button/ButtonComp';
import {
	hasInventoryManagerRole,
	toSentenceCase,
} from '../../../../common/utils/utilFunctions';

import AddMappingModal from './AddMappingModal/AddMappingModal';
import RelationMappingCard from './RelationMappingCard/RelationMappingCard';

import relationStyles from './RelationMapping.module.scss';

const { ADDED } = ENTITY_STATUS;
const { RISK_REFERENCE_TAXONOMY_NAME } = MODULE_NAME;
const { REGENERATE } = PRC_BUTTON_TEXT;
const { RELATION_MAPPING_SUCCESS } = PRC_TOAST_MSG;
const { CREATED } = HTTP_STATUS_CODES;

const RelationMappingLayout = (props: any) => {
	const dispatch = useDispatch();
	const { addNewButtonColor } = relationStyles;
	const {
		modalCloseHandler,
		isModalOpen,
		addMappingClickHandler,
		onRegenerateBtnClickHandler,
		setTabValue,
		tabValue,
		isSubmitDetailsLoading,
		isRegenerating,
		isDeleting,
		isSubmitModalOpen,
		entityType,
		entityId,
		isChangesDiscarded,
	} = props;

	const [isAllEntityDataLoading, setIsAllEntityDataLoading] =
		useState<boolean>(true);

	const payloadArray: any[] = [];
	const [isAddingRelationMapping, setIsAddingRelationMapping] =
		useState<boolean>(false);

	const loggedInUser: any = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const riskRefTaxonomySourceFiltersKey = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKey,
	);

	const hasInvManagerAccess = hasInventoryManagerRole(loggedInUser);

	const onAddBtnClickHandler = async (selectedOptions: any[]) => {
		setIsAddingRelationMapping(true);
		if (entityId) {
			dispatch(
				setAddedRelationshipMappingIds(
					selectedOptions.map((option) => option.id),
				),
			);
			// API call to add the mapping
			selectedOptions?.map((option) => {
				const data = {
					parentObjectId: entityId,
					parentObjectType: entityType,
					impactObjectId: option?.id,
					impactObjectType: option?.entityType,
					loggedInUser: loggedInUser?.email,
					reviewStatus: ADDED,
				};
				payloadArray?.push(data);
				return payloadArray;
			});
			const response = await addRelationshipMapping(payloadArray);
			setIsAddingRelationMapping(false);
			modalCloseHandler(true);
			fetchToastMsgData(response, CREATED, dispatch, RELATION_MAPPING_SUCCESS);
			fetchAllTabsData(
				entityType,
				riskRefTaxonomySourceFiltersKey,
				setIsAllEntityDataLoading,
				dispatch,
				entityId,
			);
		}
	};

	useEffect(() => {
		dispatch(setIsPRCDetailsPage(true));
		fetchAllTabsData(
			entityType,
			riskRefTaxonomySourceFiltersKey,
			setIsAllEntityDataLoading,
			dispatch,
			entityId,
		);
	}, []);

	useEffect(() => {
		if (isChangesDiscarded) {
			fetchAllTabsData(
				entityType,
				riskRefTaxonomySourceFiltersKey,
				setIsAllEntityDataLoading,
				dispatch,
				entityId,
			);
		}
	}, [isChangesDiscarded]);

	return (
		<Card
			data-testid="mapping-container"
			className={relationStyles['mapping-layout-wrapper']}
		>
			<CardContent>
				<Stack className={relationStyles['mapping-header-wrapper']}>
					<Typography className={styles['header-label']}>
						{toSentenceCase('Relationship Mapping')}
					</Typography>
					<Stack className={relationStyles['mapping-btn-container']}>
						{hasInvManagerAccess && (
							<>
								<ButtonComp
									btnType="secondary"
									compClass={relationStyles['regenerate-button']}
									startIcon={
										<Box className={relationStyles['regenerate-button-icon']}>
											<Regenerate />
										</Box>
									}
									clickHandler={onRegenerateBtnClickHandler}
									data-testid="regenerate-button"
									disabled={isSubmitDetailsLoading || isDeleting}
									loading={isRegenerating}
								>
									{REGENERATE}
								</ButtonComp>
								<ButtonComp
									btnType="secondary"
									startIcon={<Plus fill={addNewButtonColor} />}
									onClick={addMappingClickHandler}
									data-testid="add-mapping-btn"
									disabled={
										isSubmitDetailsLoading || isRegenerating || isDeleting
									}
								>
									{toSentenceCase('Add Mapping')}
								</ButtonComp>
							</>
						)}
					</Stack>
				</Stack>
				<RelationMappingCard
					childEntityOptionsData={getChildEntityOptionsData(entityType)}
					isAddRelationshipModalOpen={isModalOpen}
					parentEntityType={entityType}
					setTabValue={setTabValue}
					tabValue={tabValue}
					isSubmitModalOpen={isSubmitModalOpen}
					isRegenerating={isRegenerating}
					entityType={entityType}
					entityId={entityId}
				/>
			</CardContent>
			{isModalOpen && (
				<AddMappingModal
					isModalOpen={isModalOpen}
					modalCloseHandler={modalCloseHandler}
					parentEntityType={entityType}
					childEntityOptionsData={getChildEntityOptionsData(entityType)}
					onAddBtnClickHandler={onAddBtnClickHandler}
					moduleType={RISK_REFERENCE_TAXONOMY_NAME}
					isAddingRelationMapping={isAddingRelationMapping}
					isAllEntityDataLoading={isAllEntityDataLoading}
					entityId={entityId}
				/>
			)}
		</Card>
	);
};

export default RelationMappingLayout;
